import {
    useApplyKicoinsToCartMutation,
    useCustomerKicoinLazyQuery,
    useGetDiscountKicoinsQuery,
    useRemoveKicoinsFromCartMutation,
} from '../types/front.graphql';
import type { ApolloError } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useCart } from '../stores';

export enum KicoinActionType {
    APPLY = 'APPLY',
    REMOVE = 'REMOVE',
}
const MIN_KICOIN_TO_APPLY = 30000;

export const useCustomerKicoin = () => {
    const [getCustomerKicoin, { data, loading, error }] = useCustomerKicoinLazyQuery();
    return {
        getCustomerKicoin,
        customerKicoin: data?.customerKicoin,
        loading,
        error,
    };
};

export function useApplyKicoinsToCart() {
    const [applyKicoinsToCartMutation, { loading, error, data }] = useApplyKicoinsToCartMutation({
        onCompleted: () => {},
    });
    const applyKicoinsToCart = (cartId: string, onError?: (error: ApolloError) => void) => {
        return applyKicoinsToCartMutation({
            refetchQueries: ['CartTotal', 'GetDiscountKicoins'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
            onError,
        });
    };

    return {
        applyKicoinsToCart,
        loading,
        error,
        data,
    };
}

export function useGetDiscountKicoins(cartId: string) {
    const { data, loading, error } = useGetDiscountKicoinsQuery({
        variables: {
            cartId,
        },
    });

    return { data, loading, error };
}

export function useRemoveKicoinsFromCart() {
    const [removeKicoinsFromCartMutation, { loading, error, data }] = useRemoveKicoinsFromCartMutation({
        onCompleted: () => {},
    });
    const removeKicoinsFromCart = (cartId: string) => {
        return removeKicoinsFromCartMutation({
            refetchQueries: ['CartTotal', 'GetDiscountKicoins'],
            variables: {
                input: {
                    cart_id: cartId,
                },
            },
        });
    };

    return {
        removeKicoinsFromCart,
        loading,
        error,
        data,
    };
}

export const useKicoin = () => {
    const { cartId } = useCart();

    const { data: discountKicoins, loading: loadingDiscountKicoins } = useGetDiscountKicoins(cartId);
    const { removeKicoinsFromCart, loading: loadingRemoveKicoins } = useRemoveKicoinsFromCart();
    const { applyKicoinsToCart, loading: loadingApplyKicoins, error: errorApplyingKicoins } = useApplyKicoinsToCart();
    const { getCustomerKicoin, customerKicoin, loading: loadingCustomerKicoins } = useCustomerKicoin();

    useEffect(() => {
        getCustomerKicoin();
    }, [customerKicoin, getCustomerKicoin]);

    const discountValue = useMemo(() => Number(discountKicoins?.cart?.discount_kicoins?.value ?? 0), [discountKicoins]);
    const appliedKicoins = discountValue;
    const title = discountKicoins?.cart?.discount_kicoins?.title;
    const isUseKiCoin = useMemo(() => appliedKicoins < 0, [appliedKicoins]);

    const kicoinDecrease = useMemo(() => {
        return discountKicoins?.cart?.discount_kicoins?.value ?? 0;
    }, [discountKicoins]);

    const canApplyKicoins = useMemo(
        () => (customerKicoin?.balance ?? 0) >= MIN_KICOIN_TO_APPLY,
        [customerKicoin?.balance]
    );

    const handleApplyKicoins = useCallback(async () => {
        if (!cartId) return;
        try {
            await applyKicoinsToCart(cartId);
        } catch (error) {
            console.error('Error applying Kicoins:', error);
        }
    }, [applyKicoinsToCart, cartId]);

    const handleRemoveKicoins = useCallback(async () => {
        if (!cartId) return;
        try {
            await removeKicoinsFromCart(cartId);
        } catch (error) {
            console.error('Error removing Kicoins:', error);
        }
    }, [removeKicoinsFromCart, cartId]);

    const handleKicoinAction = useCallback(
        (actionType: KicoinActionType) => {
            switch (actionType) {
                case KicoinActionType.APPLY:
                    handleApplyKicoins();
                    break;
                case KicoinActionType.REMOVE:
                    handleRemoveKicoins();
                    break;
                default:
                    break;
            }
        },
        [handleApplyKicoins, handleRemoveKicoins]
    );

    const loading = useMemo(() => {
        return loadingCustomerKicoins;
    }, [loadingCustomerKicoins]);

    return {
        title,
        loading,
        kicoinDecrease,
        appliedKicoins,
        isUseKiCoin,
        canApplyKicoins,
        loadingDiscountKicoins,
        loadingApplyKicoins: loadingApplyKicoins,
        loadingRemoveKicoins,
        errorApplyingKicoins,
        handleKicoinAction,
        handleApplyKicoins,
        handleRemoveKicoins,
    };
};
